import { get as dig, isEqual } from "lodash"
import { removeLocalStorageValue } from "source/shared/hooks/useLocalStorage"

import type {
  ChurchCenterWebMenuAPIRecord,
  OrganizationAPIRecord,
  PageAPIRecord,
  ThemeSettingsAPIRecord,
  WebBootAPIResponse,
  WebBootIncludedRecord,
  WebBootRelationships,
} from "source/publishing/WebBoot/types"

export const WEB_BOOT_LOCAL_STORAGE_KEY = "WebBoot.v9"
const PREVIOUS_WEB_BOOT_LOCAL_STORAGE_KEYS = [
  "WebBoot.v8",
  "WebBoot.v7",
  "WebBoot.v6",
  "WebBoot.v5",
  "WebBoot.v4",
  "WebBoot.v3",
  "WebBoot.v2",
  "WebBoot.v1",
]

const WEB_BOOT_INCLUDES = [
  "draft_menu",
  "draft_home_page",
  "draft_theme_settings",
  "published_menu",
  "published_home_page",
  "published_theme_settings",
  "current_organization",
]
export const WEB_BOOT_PATH = `/publishing/v2/web_boot?include=${WEB_BOOT_INCLUDES}`

export function mapApiResponseToStateShape(json: WebBootAPIResponse) {
  const extractRelationship = <T extends WebBootIncludedRecord>(
    name: keyof WebBootRelationships,
  ): T | undefined => {
    const type = dig(json, `data.relationships.${name}.data.0.type`)
    const id = dig(json, `data.relationships.${name}.data.0.id`)
    return json.included.find((i) => i.type === type && i.id === id) as
      | T
      | undefined
  }

  // If the WebBoot state shape changes, WEB_BOOT_LOCAL_STORAGE_KEY _must_ be increased
  // and the old key added to the PREVIOUS_WEB_BOOT_LOCAL_STORAGE_KEYS array above.
  return {
    abilities: dig(json, "data.attributes.abilities"),
    analyticsUrl: dig(json, "data.attributes.analytics_url"),
    builtInPages: dig(json, "data.attributes.built_in_pages"),
    currentOrganization: extractRelationship<OrganizationAPIRecord>(
      "current_organization",
    ),
    draftHomePage: extractRelationship<PageAPIRecord>("draft_home_page"),
    draftMenu: extractRelationship<ChurchCenterWebMenuAPIRecord>("draft_menu"),
    draftThemeSettings: extractRelationship<ThemeSettingsAPIRecord>(
      "draft_theme_settings",
    ),
    features: dig(json, "data.attributes.features"),
    joltWebsocketUrl: dig(json, "data.attributes.jolt_websocket_url"),
    myChurchCenterEnabled: dig(
      json,
      "data.attributes.my_church_center_enabled",
    ),
    publishedHomePage: extractRelationship<PageAPIRecord>(
      "published_home_page",
    ),
    publishedMenu:
      extractRelationship<ChurchCenterWebMenuAPIRecord>("published_menu"),
    publishedThemeSettings: extractRelationship<ThemeSettingsAPIRecord>(
      "published_theme_settings",
    ),
  }
}

export type WebBootState = ReturnType<typeof mapApiResponseToStateShape>

export const webBootDataDifferent = (
  prev: WebBootState,
  next: WebBootState,
) => {
  const attributesToIgnore = ["draftHomePage", "publishedHomePage"]
  if (prev.draftHomePage?.id !== next.draftHomePage?.id) return true
  if (prev.publishedHomePage?.id !== next.publishedHomePage?.id) return true
  return (Object.keys(next) as Array<keyof WebBootState>).some((key) => {
    if (attributesToIgnore.includes(key)) return false
    return !isEqual(prev[key], next[key])
  })
}

export function setLocalWebBootState(webBootState: WebBootState) {
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(
      WEB_BOOT_LOCAL_STORAGE_KEY,
      JSON.stringify(webBootState),
    )
  }
}

export function getLocalWebBootState(): WebBootState | null {
  if (typeof window === "undefined") return null
  const webBootState = window.localStorage?.getItem(WEB_BOOT_LOCAL_STORAGE_KEY)
  if (!webBootState) return null
  try {
    return JSON.parse(webBootState)
  } catch (e) {
    console.error("Failed to parse local web boot state", e)
    return null
  }
}

export function resetLocalWebBootState() {
  removeLocalStorageValue(WEB_BOOT_LOCAL_STORAGE_KEY)
}

export function clearPreviousLocalWebBootState() {
  PREVIOUS_WEB_BOOT_LOCAL_STORAGE_KEYS.forEach((key) => {
    removeLocalStorageValue(key)
  })
}
